
import { toast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import AppLayout from "@/components/layout/AppLayout";
import { supabase } from "@/integrations/supabase/client";
import { User } from "lucide-react";
import { UsersList } from "./components/UsersList";
import { useUsers } from "./hooks/useUsers";
import { useUserRoles } from "@/hooks/useUserRoles";
import type { UserRole } from "./types";

const UsersPage = () => {
  const { isAdmin, profiles, isLoadingProfiles, refetch } = useUsers();
  const { isManager } = useUserRoles();

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    try {
      // Update the profile Role column if it exists
      const { error: profileUpdateError } = await supabase
        .from("profiles")
        .update({ Role: newRole })
        .eq("id", userId);

      if (profileUpdateError) {
        console.error("Error updating profile role:", profileUpdateError);
        // Continue even if profile update fails as it might be a nullable column
      }

      // Delete existing roles
      const { error: deleteError } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", userId);

      if (deleteError) throw deleteError;

      // Insert new role
      const { error: insertError } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role: newRole });

      if (insertError) throw insertError;

      toast({
        title: "Role Updated",
        description: "User role has been updated successfully",
      });

      // Refresh the profiles list
      await refetch();
    } catch (error: any) {
      console.error("Error updating role:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (!isAdmin && !isManager) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Card className="p-6">
            <p className="text-red-500">Access Denied: Administrators and Managers only</p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (isLoadingProfiles) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Card className="p-6">
            <p className="text-gray-500 dark:text-gray-300">Loading users...</p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h2 className="text-2xl font-semibold tracking-tight dark:text-white">Users</h2>
            <p className="text-sm text-muted-foreground dark:text-gray-300">
              {isManager && !isAdmin 
                ? "View user information and team assignments" 
                : "Manage user roles and permissions"}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" className="dark:text-white dark:border-gray-600">
              <User className="mr-2 h-4 w-4" />
              Total Users: {profiles?.length || 0}
            </Button>
          </div>
        </div>

        {profiles && (
          <UsersList
            profiles={profiles}
            isAdmin={isAdmin}
            onRoleChange={handleRoleChange}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default UsersPage;
